<script setup lang="ts">
import { computed, toRef } from 'vue';
import type { PageName } from '@/api/tracking';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import { useSurveyAccordion } from '@/components/survey/composables/useSurveyHistoryCommon';
import DeepProcessMessage from '@/components/survey/generate-content/deep-process-message.vue';
import SurveyContentRenderer from '@/components/survey/generate-content/survey-content-renderer.vue';
import SurveyFeedback from '@/components/survey/survey-feedback.vue';
import type {
  AdpDocument,
  DeepModeProcess,
  FeedbackType,
  Part,
  SurveyHistoryStatus,
  SurveyRequestState,
  UserDocument,
} from '@/types';
import { checkRequestState, parseModelResponse } from '@/utils/survey/common';
import {
  SURVEY_ERROR_MESSAGES,
  SURVEY_FEATURE_NAME,
} from '@/utils/survey/constants';

interface Props {
  sessionId: string;
  requestId: string;
  question: string;
  incomplete: boolean;
  sources: Array<AdpDocument | UserDocument>;
  deepModeProcesses: Array<DeepModeProcess>;
  reasoning: Array<Part>;
  originMarkdownReasoning: string;
  originMarkdownSummary: string;
  summary: Array<Part>;
  enableAnimation: boolean;
  enableFeedback: boolean;
  feedbackType: FeedbackType | undefined;
  status: SurveyHistoryStatus;
  requestState?: SurveyRequestState;
  pageName: PageName;
}

const props = defineProps<Props>();

// スレッドを開いた初期表示では、並列にパース処理を行うことでパフォーマンスを向上させる
const computedSummary = computed(() => {
  return parseModelResponse(
    props.originMarkdownSummary,
    props.sources,
    false,
    false,
  );
});

// 推論（CoT）もしくは途中経過メッセージのセクション全体を表示するかどうか
const isProgressReasoningVisible = computed(() => {
  return !props.status.isNoHit || props.incomplete;
});

// アコーディオンの自動開閉機能
const { isSummaryVisible, isAccordionOpen, accordionRef, toggleAccordion } =
  useSurveyAccordion({
    enableAnimation: toRef(props, 'enableAnimation'),
    reasoning: toRef(props, 'reasoning'),
    summary: toRef(props, 'summary'),
  });

const enableAction = computed(() => {
  return (
    props.requestId &&
    !props.status.isInProgress &&
    !props.status.isError &&
    !props.incomplete &&
    checkRequestState(
      props.requestState,
      ['stopping', 'stopped', 'aborted'],
      false,
    )
  );
});

const showFeedbackButton = computed(() => {
  return props.enableFeedback && enableAction.value;
});

const errorMessage = computed(
  () =>
    SURVEY_ERROR_MESSAGES[props.status.errorCause ?? ''] ??
    SURVEY_ERROR_MESSAGES.default,
);
</script>

<template>
  <div class="answer-content">
    <div
      v-if="isProgressReasoningVisible"
      class="survey-history-content c-text-regular--m"
    >
      <div class="progress-reasoning-wrapper">
        <div class="accordion-toggle" @click="toggleAccordion">
          <div class="accordion-icon-and-text">
            <DgrIcon name="deep-research" size="small" class="shrink-guard" />
            <span>ディープリサーチプロセス</span>
          </div>
          <DgrIcon
            name="chevron-down"
            size="small"
            class="toggle-icon"
            :class="{ 'is-open': isAccordionOpen }"
          />
        </div>
        <div
          ref="accordionRef"
          class="accordion-content-wrapper"
          :class="{ 'is-open': isAccordionOpen }"
          style="
            --survey-heading-font-size: 14px;
            --survey-text-font-size: 14px;
          "
        >
          <div class="accordion-content" v-if="!props.status.isError">
            <div class="accordion-icon-and-text notification-message">
              <DgrIcon
                name="bell"
                size="small"
                :keep-fill="false"
                class="shrink-guard"
              />
              <span
                >このページから離れても生成を継続します。完了後に通知を送ります。</span
              >
            </div>
            <template
              v-for="(process, index) in props.deepModeProcesses"
              :key="index"
            >
              <div class="deep-research-process accordion-icon-and-text">
                <DgrIcon
                  :name="
                    process.message_type === 'query'
                      ? 'search'
                      : 'sparkles-fill'
                  "
                  size="small"
                  :keep-fill="false"
                  class="shrink-guard"
                />
                <DeepProcessMessage
                  :message="process.message"
                  :sources="props.sources"
                  :page-name="props.pageName"
                />
              </div>
            </template>
            <div
              class="deep-research-process accordion-icon-and-text"
              v-if="props.originMarkdownReasoning"
            >
              <DgrIcon
                name="sparkles-fill"
                size="small"
                :keep-fill="false"
                class="shrink-guard"
              />
              <DeepProcessMessage
                :message="props.originMarkdownReasoning"
                :sources="props.sources"
                :page-name="props.pageName"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="survey-history-content">
      <SurveyContentRenderer
        v-if="
          (computedSummary.length > 0 || props.summary.length > 0) &&
          isSummaryVisible
        "
        v-memo="[props.summary, props.enableAnimation, props.status]"
        :content-parts="props.enableAnimation ? props.summary : computedSummary"
        :enable-animation="props.enableAnimation"
        :status="props.status"
        :contents-context="{
          event_name: SURVEY_FEATURE_NAME,
          session_id: props.sessionId,
        }"
        feature="survey"
        :page-name="props.pageName"
      />
      <template v-else-if="props.status.isNoHit && !props.incomplete">
        <p>
          「{{ props.question }}」
          に関するソースを見つけられなかったため要約を生成できませんでした。
        </p>
        <p>
          質問をより明確にするか、要約に使用する情報源を変更することで要約を作成できる可能性があります。
        </p>
        <div class="spacing-08"></div>
      </template>
      <template v-else-if="props.status.isError">
        <p>
          {{ errorMessage }}
        </p>
        <div class="spacing-08"></div>
      </template>
      <div class="summary-feedback">
        <SurveyFeedback
          v-if="showFeedbackButton"
          :session-id="props.sessionId"
          :request-id="props.requestId"
          :feedback-type="props.feedbackType"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.answer-content {
  margin: 16px 0 8px;
  position: relative;
}

.survey-history-content {
  padding: 0 16px;
}

.progress-reasoning-wrapper {
  font-size: 14px;
  border: 1px solid $color-gray400;
  border-radius: 4px;
  margin-bottom: 16px;
}

.shrink-guard {
  flex-shrink: 0;
}

.accordion-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding: 16px;
  cursor: pointer;
  user-select: none;
}

.notification-message {
  margin-bottom: 18px;
}

.deep-research-process {
  margin-bottom: 24px;
}

.deep-research-process:last-child {
  margin-bottom: 0;
}

.accordion-icon-and-text {
  display: flex;
  align-items: center;
  gap: 8px;
  align-items: flex-start;
}

.accordion-icon-and-text .shrink-guard {
  align-self: flex-start;
  margin-top: 4px; // deep-research-processアイコン位置の微調整
}

.toggle-icon {
  transition: transform 0.3s;
  &.is-open {
    transform: rotate(180deg);
  }
}

.accordion-content-wrapper {
  display: block;
  overflow: hidden;
  max-height: 0;
  transition: max-height 0.3s ease;
  transform-origin: top;
}
.accordion-content-wrapper.is-open {
  max-height: 20000px; // NOTE: 暫定対応としてコンテンツが収まりきるであろう大きな値を設定している。タブに分ける等のベストな方法は後ほど検討する
}

.accordion-content {
  padding: 4px 16px 16px;
}

.summary-feedback {
  background-color: $color-gray200;
  border-radius: 4px;
}
</style>
